import {DomainDefaultsInfo} from "@/api";
import {useDomainNameQuery} from "@/queries.generated";
import {
    DomainDefaultsUpdateMutation,
    DomainDefaultsUpdateMutationVariables,
    useDomainDefaultsDataSourceQuery,
    useDomainDefaultsQuery,
    useDomainDefaultsUpdateMutation,
} from "./queries.generated";
import {useCallback, useState} from "react";
import {FormActionButtons, FormattedFormRow, FormContainer, LoadingIcon} from "@dvrt/graphql-widgets";
import {Form} from "@/utils/Form";
import {FormattedPageTitle} from "@/components/pages";
import {formPagesWidth} from "@/constants";
import {ListItemText, MenuItem, Select} from "@mui/material";
import {defineMessage, MessageDescriptor, useIntl} from "react-intl";
import {useDispatch} from "react-redux";
import {showNotification} from "@/actions";
import {commonTranslations} from "@/utils/translations";
import {useAvailableTranslationsQuery} from "../bulk_extension/bulkextensionqueries.generated";
import LanguageSelector from "@/components/LanguageSelector";

type DataSource = {
    loading: boolean;
    refetch: () => Promise<DomainDefaultsUpdateMutationVariables>;

    domainId: number;
    domainName: string;

    defaults: DomainDefaultsInfo;
    vendors: IdNameItem<number>[];
    deviceVariants: IdNameItem<number>[];
    languages: string[];

    initialData: DomainDefaultsUpdateMutationVariables;
};

function useDataSource(domainId: number): DataSource {
    const domainNameQuery = useDomainNameQuery({
        variables: {
            id: domainId,
        },
        fetchPolicy: "no-cache",
    });

    const domainDefaultsQuery = useDomainDefaultsQuery({
        variables: {
            domainId,
        },
        fetchPolicy: "no-cache",
    });

    const vendorsAndVariantsQuery = useDomainDefaultsDataSourceQuery({
        variables: {
            domainId,
        },
        fetchPolicy: "no-cache",
    });

    const languagesQuery = useAvailableTranslationsQuery();

    const refetch = useCallback(async () => {
        const {data: newData} = await domainDefaultsQuery.refetch();
        const newInitialData: DomainDefaultsUpdateMutationVariables = {
            domainId,
            vendorId: newData.domainDefaults.vendor.id,
            deviceVariantId: newData.domainDefaults.deviceVariant?.id,
            language: newData.domainDefaults.language,
        };
        return newInitialData;
    }, [domainDefaultsQuery, domainId]);

    if (
        domainDefaultsQuery.loading ||
        domainNameQuery.loading ||
        vendorsAndVariantsQuery.loading ||
        languagesQuery.loading
    )
        return {
            loading: true,
            refetch,
            domainId,
            domainName: "",
            defaults: undefined,
            vendors: [],
            deviceVariants: [],
            languages: [],
            initialData: undefined,
        };

    return {
        loading: false,
        refetch,
        domainId,
        domainName: domainNameQuery.data.domains.items[0].name,
        defaults: domainDefaultsQuery.data.domainDefaults,
        vendors: vendorsAndVariantsQuery.data.vendors,
        deviceVariants: vendorsAndVariantsQuery.data.deviceVariants.items,
        languages: languagesQuery.data.availableTranslations,
        initialData: {
            domainId,
            vendorId: domainDefaultsQuery.data.domainDefaults.vendor.id,
            deviceVariantId: domainDefaultsQuery.data.domainDefaults.deviceVariant?.id,
            language: domainDefaultsQuery.data.domainDefaults.language,
        },
    };
}

class DomainDefaultsForm extends Form<DomainDefaultsUpdateMutation, DomainDefaultsUpdateMutationVariables> {
    constructor(initialData: DomainDefaultsUpdateMutationVariables) {
        super("domainDefaultsUpdate");
        this.hooks(useDomainDefaultsUpdateMutation, initialData);
    }

    protected override get filteredVariables(): DomainDefaultsUpdateMutationVariables {
        const filtered = structuredClone(this.values);
        const initialData = this.initialData;

        if (filtered.vendorId === initialData.vendorId) delete filtered.vendorId;
        if (filtered.deviceVariantId === initialData.deviceVariantId) delete filtered.deviceVariantId;
        else if (filtered.deviceVariantId === 0) filtered.deviceVariantId = null;

        return filtered;
    }
}

interface IdNameItem<ID> {
    id: ID;
    name: string;
}

type Props = {
    domainId: number;
};

const ItemSelect = <Item extends string | number>({
    selected,
    onSelect,
    items: baseItems,
    disabled = false,
    defaultMessage,
}: {
    selected: Item | undefined;
    onSelect: (value: string | undefined) => void;
    items: IdNameItem<Item>[];
    disabled?: boolean;
    defaultMessage?: MessageDescriptor;
}) => {
    const intl = useIntl();

    const items = defaultMessage
        ? [{id: "" as Item, name: intl.formatMessage(defaultMessage)}, ...baseItems]
        : baseItems;

    return (
        <Select
            variant="standard"
            value={selected || ""}
            disabled={disabled}
            onChange={(e) => onSelect(e.target.value === "" ? undefined : (e.target.value as string | undefined))}
            displayEmpty={defaultMessage !== undefined}
            renderValue={(selected) => {
                if (!selected) {
                    return intl.formatMessage(defaultMessage);
                }
                return items.find((item) => item.id === selected).name;
            }}
        >
            {items.map((item) => {
                return (
                    <MenuItem key={item.id} value={item.id}>
                        <ListItemText primary={item.name} />
                    </MenuItem>
                );
            })}
        </Select>
    );
};

const FormContent = ({dataSource}: {dataSource: DataSource}) => {
    const [submitting, setSubmitting] = useState(false);
    const form = new DomainDefaultsForm(dataSource.initialData);
    const dispatch = useDispatch();

    const handleSubmit = async () => {
        if (submitting) return;
        setSubmitting(true);

        if (await form.submit()) {
            form.newInitialData(await dataSource.refetch());
        } else {
            // this should not happen, but log out the errors in case
            console.error("Error submitting form", form.errors);
            dispatch(
                showNotification(defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}), "error"),
            );
        }

        setSubmitting(false);
    };

    return (
        <div>
            <FormattedPageTitle
                desc={defineMessage({
                    id: "domain_settings.page_titles.default",
                    defaultMessage: "Default values for domain {domain}",
                })}
                values={{domain: dataSource.domainName}}
            />
            <FormContainer style={{width: formPagesWidth, gap: "10px"}}>
                <FormattedFormRow label={defineMessage({id: "vendor", defaultMessage: "Vendor"})}>
                    <ItemSelect
                        selected={form.values.vendorId}
                        onSelect={(id) => form.updateValues({vendorId: Number(id)})}
                        disabled={submitting}
                        items={dataSource.vendors}
                    />
                </FormattedFormRow>
                <FormattedFormRow label={defineMessage({id: "deviceVariant", defaultMessage: "Device variant"})}>
                    <ItemSelect
                        selected={form.values.deviceVariantId}
                        onSelect={(id) => form.updateValues({deviceVariantId: id ? Number(id) : undefined})}
                        disabled={submitting}
                        items={dataSource.deviceVariants}
                        defaultMessage={commonTranslations.default}
                    />
                </FormattedFormRow>
                <FormattedFormRow label={commonTranslations.language}>
                    <LanguageSelector
                        value={form.values.language || ""}
                        variant="standard"
                        onChange={(language) => form.updateValues({language: language || null})}
                        disabled={submitting}
                        languages={dataSource.languages}
                        displayEmpty // allow selecting system default
                    />
                </FormattedFormRow>
                <FormattedFormRow inputStyle={{marginTop: "10px"}}>
                    <FormActionButtons
                        mainAction={handleSubmit}
                        secondaryAction={() => form.reset()}
                        enabled={form.hasChanged && !submitting}
                    />
                </FormattedFormRow>
            </FormContainer>
        </div>
    );
};

const DomainDefaults = ({domainId}: Props) => {
    const dataSource = useDataSource(domainId);

    if (dataSource.loading) {
        return <LoadingIcon />;
    }

    return <FormContent dataSource={dataSource} />;
};

export default DomainDefaults;
